import * as React from 'react';
import axios from 'axios';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import { Alert } from '@material-ui/lab';
import { Box, Button, styled, TextField, Typography } from '@material-ui/core';
import { get } from 'lodash';
import { Model, Question } from 'survey-core';
import { SurveyModel } from 'survey-core/survey.core';
import { SurveyQuestionElementBase } from 'survey-react-ui';
import { ValueChangedEvent } from 'survey-core/typings/survey-events-api';
import { OperationType, OrganizationType } from '../../types/enums';
import { checkFoundation } from '../../helpers/common';

export const TYPE = 'mui-discount';

export enum DiscountCodeErrorKey {
  CODE_NOT_FOUND = '1',
  CODE_EXPIRED = '2',
  CODE_CANNOT_BE_APPLIED = '3',
  CLIENT_NOT_FOUND = '4',
  CODE_IS_NOT_VALID = '5'
}

export interface IDiscount {
  id: string;
  code: string;
  amount: number;
  clientId?: string;
  appliesTo: string[];
}

export class MuiDiscountModel extends Question {
  getType(): string {
    return TYPE;
  }

  get validatorUrl() {
    return this.getPropertyValue('validatorUrl');
  }
  set validatorUrl(val) {
    this.setPropertyValue('validatorUrl', val);
  }

  get strings() {
    return this.getPropertyValue('strings');
  }
  set strings(val) {
    this.setPropertyValue('strings', val);
  }
}

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(-0.25),
  '& span': {
    fontWeight: theme.typography.fontWeightMedium
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  border: `1px solid var(--on-surface-outline-000000-12-)`,
  width: '75px',
  marginTop: theme.spacing(1),
  '&:disabled': {
    '& .MuiButton-label': {
      color: 'var(--on-surface-disabled-000000-38-)'
    }
  },
  '&:hover': {
    borderColor: 'var(--on-surface-outline-000000-12-)'
  },
  '& .MuiButton-label': {
    color: 'var(--primary-blue-six)'
  }
}));

const StyledAlert = styled(Alert)(() => ({
  backgroundColor: 'var(--light-gray-surface)',
  width: '378px',
  height: '64px',
  boxSizing: 'border-box',
  borderRadius: '12px',
  alignItems: 'center',
  '& .MuiAlert-icon': {
    fontSize: '2.5rem'
  },
  '& .MuiAlert-message': {
    color: 'var(--black-text)',
    '& p span': {
      fontWeight: 600
    }
  },
  '& .MuiButton-root': {
    textTransform: 'uppercase',
    fontSize: 'var(--font-size-14)',
    lineHeight: 'var(--line-height-16)',
    letterSpacing: 'var(--character-spacing-1-25)',
    fontWeight: 500
  }
}));

const STORED_DISCOUNT_DATA = 'survey:storedDiscountData_';
export const getErrorKeyByCode = (code: DiscountCodeErrorKey) =>
  ({
    [DiscountCodeErrorKey.CODE_NOT_FOUND]: 'codeNotFound',
    [DiscountCodeErrorKey.CODE_EXPIRED]: 'codeExpired',
    [DiscountCodeErrorKey.CODE_CANNOT_BE_APPLIED]: 'codeCannotByApplied',
    [DiscountCodeErrorKey.CLIENT_NOT_FOUND]: 'clientNotFound',
    [DiscountCodeErrorKey.CODE_IS_NOT_VALID]: 'codeIsNotValid'
  }[code] || 'unknownError');

export class MuiDiscount extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.valueChangedHandler = this.valueChangedHandler.bind(this);
    this.checkDiscountActive();
  }

  componentDidMount() {
    super.componentDidMount();
    (this.question.survey as Model).onValueChanged.add(this.valueChangedHandler);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    const model = this.question.survey as Model;

    model.setVariable('discountParams', JSON.stringify(this.getRequestBody()));
    model.onValueChanged.remove(this.valueChangedHandler);
  }

  protected valueChangedHandler(model: SurveyModel, options: ValueChangedEvent) {
    const subscriptionChanged = options.name === 'subscriptionSelect' && !!options.value;
    const additionalProductChanged = options.name === 'additionalProducts' && !!options.value;

    if (subscriptionChanged) {
      this.handleRemove();
      return;
    }

    // Need to reflect discount changes when switching between support packages if the discount is applied to each item
    if (additionalProductChanged) {
      if (this.question.errors.length > 0) {
        this.question.clearErrors();
      }
      model.currentPage.setPropertyValue('forceUpdated', Date.now());
    }
  }

  checkDiscountActive() {
    const model = this.question.survey as Model;
    const prevDiscountParams = model.getVariable('discountParams');

    if (!!prevDiscountParams && prevDiscountParams !== JSON.stringify(this.getRequestBody())) {
      this.handleRemove();
    }
  }

  get question() {
    return this.questionBase;
  }

  get validatorUrl() {
    return this.question.validatorUrl;
  }

  getString(key: string) {
    return get(this.question, ['strings', key], key);
  }

  handleChange({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    if (value === ' ') return;
    this.question.value = value;
  }

  handleBlur({ target: { value } }: React.FocusEvent<HTMLInputElement>) {
    this.question.value = value ? value.trim() : value;
  }

  getRequestBody() {
    const model = this.question.survey as Model;
    const filteredProducts = model.getVariable('filteredProducts') || {};
    const selectedProducts = [
      model.runExpression('expandChoicesByUrl({subscriptionSelect}, id)'),
      model.runExpression("getJoiningFee('id')")
    ];
    const { location, operationType, currentSubscription } = model.getVariable('info');
    const isFoundation = checkFoundation(currentSubscription);
    let applicationType =
      model.data['applicationType'] || model.runExpression('expandChoicesByUrl({subscriptionSelect}, applicationType)');
    const assessmentType =
      model.data['assessmentType'] ||
      model.runExpression('expandChoicesByUrl({subscriptionSelect}, assessmentType)')?.replace('CHAS ', '');

    if (Array.isArray(filteredProducts.additionalProducts)) {
      selectedProducts.push(...filteredProducts.additionalProducts);
    }

    if (isFoundation && model.data['organizationType'] === OrganizationType.Supplier) {
      applicationType = OrganizationType.Supplier;
    }

    return {
      location,
      applicationType,
      assessmentType,
      subscriptionTier: model.runExpression(`expandChoicesByUrl({subscriptionSelect}, subscriptionTier)`),
      operationType: isFoundation ? OperationType.SignUp : operationType,
      selectedProducts: selectedProducts.filter(value => !!value)
    };
  }

  handleApply() {
    const model = this.question.survey as Model;
    const { operationType, organizationId } = model.getVariable('info');
    this.question.clearErrors();

    if (this.validatorUrl) {
      const body = {
        code: this.question.value,
        ...this.getRequestBody()
      };
      this.question.setPropertyValue('codeIsChecking', true);
      axios
        .post<IDiscount>(this.validatorUrl, body)
        .then(response => {
          const discount = response.data;

          model.setVariable('discount', discount);
          this.question.setPropertyValue('appliedCode', this.question.value);
          sessionStorage.setItem(`${STORED_DISCOUNT_DATA}${organizationId}_${operationType}`, JSON.stringify(discount));
          model.currentPage.setPropertyValue('forceUpdated', Date.now());
        })
        .catch(error => {
          if (error.response.status === 406) {
            this.question.addError(this.getString(getErrorKeyByCode(get(error.response, ['data'], ''))));
            return;
          }
          this.question.addError(this.getString('unknownError'));
        })
        .finally(() => {
          this.question.setPropertyValue('codeIsChecking', false);
        });
    }
  }

  handleRemove() {
    const model = this.question.survey as Model;
    const { operationType, organizationId } = model.getVariable('info');

    model.setVariable('discount', null);
    this.question.setPropertyValue('appliedCode', null);
    this.question.clearValue();
    this.question.clearErrors();
    sessionStorage.removeItem(`${STORED_DISCOUNT_DATA}${organizationId}_${operationType}`);
    model.currentPage.setPropertyValue('forceUpdated', Date.now());
  }

  protected renderElement(): JSX.Element | null {
    const { value = '', title, isRequired, isReadOnly } = this.question;
    const appliedCode = this.question.getPropertyValue('appliedCode');
    const model = this.question.survey as Model;
    const codeIsChecking = this.question.getPropertyValue('codeIsChecking');
    const relativeDiscountAmount = model.calculatedValues.find(
      calculatedValue => calculatedValue.name === 'relativeDiscountAmount'
    );
    const discount = model.getVariable('discount');

    if (appliedCode) {
      return (
        <StyledAlert
          severity="info"
          icon={<LocalOfferIcon color="primary" fontSize="inherit" />}
          action={
            <Button color="primary" size="small" onClick={this.handleRemove}>
              {this.getString('remove')}
            </Button>
          }
        >
          <StyledTypography>
            <span>{appliedCode}</span> {this.getString('codeApplied')}
          </StyledTypography>
          {discount?.amount > 0 && relativeDiscountAmount && (
            <Typography variant="caption">{relativeDiscountAmount.value}</Typography>
          )}
        </StyledAlert>
      );
    }

    return (
      <Box display="flex" alignItems="start" mt={1} mb={1}>
        <TextField
          label={title}
          value={value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          required={isRequired}
          variant="filled"
          InputProps={{
            readOnly: this.isDisplayMode
          }}
          inputProps={{
            style: { textTransform: 'uppercase' }
          }}
          style={{ width: '210px' }}
          disabled={isReadOnly}
        />
        <Box flexShrink={0} marginTop={0.25} marginLeft={2}>
          <StyledButton
            variant="outlined"
            color="primary"
            onClick={this.handleApply}
            disabled={codeIsChecking || !value || isReadOnly}
          >
            {this.getString('apply')}
          </StyledButton>
        </Box>
      </Box>
    );
  }
}
